import { transitionToStale } from "@/features/engagement/engagement-state";
import { ENGAGEMENTS_TABLE, supabase } from "@/lib/supabaseClient";
import type {
  ComputeLogEntry,
  EngagementAccessMeta,
  EngagementHistoryEntry,
  EngagementMember,
  EngagementMemberRole,
  EngagementWithAccess,
  HistoryEntry,
  PendingTierRequest,
  TierChangeRequest,
  UsageStats,
  UserProfile,
} from "@/types";

import type { HistoryStorage } from "../interfaces";

import {
  LocalStorageHistoryStorage,
  reindexAfterRemoval,
} from "./LocalStorageHistoryStorage";

const PURGE_AFTER_DAYS = 7;
const SCHEMA_VERSION = 1;
const CONTRACTS_TABLE = "contracts";
const PROFILES_TABLE = "profiles";
const MEMBERS_TABLE = "engagement_members";
const TIER_REQUESTS_TABLE = "tier_change_requests";

interface TierRequestRow {
  id: string;
  user_id: string;
  current_tier: string;
  requested_tier: string;
  status: string;
  note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
}

function toTierChangeRequest(row: TierRequestRow): TierChangeRequest {
  return {
    id: row.id,
    userId: row.user_id,
    currentTier: row.current_tier,
    requestedTier: row.requested_tier,
    status: row.status,
    note: row.note,
    reviewedBy: row.reviewed_by,
    reviewedAt: row.reviewed_at,
    reviewNote: row.review_note,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

interface EngagementRow {
  id: string;
  owner_id: string | null;
  client_name: string;
  audit_period_start: string;
  audit_period_end: string;
  functional_currency: string;
  status: string;
  is_deleted: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  data: EngagementHistoryEntry;
}

function toRow(entry: EngagementHistoryEntry): Omit<EngagementRow, "owner_id"> {
  return {
    id: entry.id,
    client_name: entry.clientName,
    audit_period_start: entry.auditPeriodStart,
    audit_period_end: entry.auditPeriodEnd,
    functional_currency: entry.functionalCurrency,
    status: entry.status ?? "draft",
    is_deleted: entry.isDeleted,
    deleted_at: entry.deletedAt ?? null,
    created_at: entry.createdAt,
    updated_at: entry.updatedAt,
    data: entry,
  };
}

/**
 * Supabase-backed history storage (SPEC-14b / FAZ 1).
 *
 * Engagement methods persist to the `engagements` table in Supabase; the
 * `data` jsonb column holds the FULL `EngagementHistoryEntry` object for
 * lossless round-trip, while top-level columns are denormalised copies
 * used only for filtering and ordering.
 *
 * Legacy single-contract methods (`list` / `add` / `delete` / `deleteMany`
 * / `clear`) remain backed by localStorage via a delegated
 * `LocalStorageHistoryStorage` instance — that path is deprecated and
 * intentionally not migrated.
 */
export class SupabaseHistoryStorage implements HistoryStorage {
  private readonly local = new LocalStorageHistoryStorage();

  // ── Single-contract (delegated) ──────────────────────────────────────────

  async list(): Promise<HistoryEntry[]> {
    return this.local.list();
  }

  async add(entry: HistoryEntry): Promise<void> {
    return this.local.add(entry);
  }

  async delete(id: string): Promise<void> {
    return this.local.delete(id);
  }

  async deleteMany(ids: string[]): Promise<void> {
    return this.local.deleteMany(ids);
  }

  async clear(): Promise<void> {
    return this.local.clear();
  }

  async exportJson(): Promise<Blob> {
    const entries = await this.local.list();
    const engagements = await this.listEngagements();
    const json = JSON.stringify(
      {
        version: SCHEMA_VERSION,
        exportedAt: new Date().toISOString(),
        entries,
        engagements,
      },
      null,
      2,
    );
    return new Blob([json], { type: "application/json" });
  }

  // ── Engagement CRUD ──────────────────────────────────────────────────────

  async listEngagements(): Promise<EngagementWithAccess[]> {
    // SPEC-22 / SPEC-SHARE — the dashboard list returns engagements the
    // user OWNS or is a MEMBER of (shared with them). It deliberately does
    // NOT rely on the RLS admin override: an admin still sees only their
    // own + shared here; the everyone-view lives in the admin panel
    // (SPEC-23). `getEngagement(id)` stays UNFILTERED so admins can open
    // borrowed engagements by id; RLS enforces access there.
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) return [];

    // Engagement ids the user is a member of (shared with them), with the
    // user's own role + can_invite for the `_access` decoration below.
    const { data: memberRows, error: memberErr } = await supabase
      .from(MEMBERS_TABLE)
      .select("engagement_id, role, can_invite")
      .eq("user_id", uid);
    if (memberErr) {
      throw new Error(
        `Supabase listEngagements (members) failed: ${memberErr.message}`,
      );
    }
    const memberMeta = new Map<
      string,
      { role: EngagementMemberRole; can_invite: boolean }
    >(
      (
        (memberRows ?? []) as Array<{
          engagement_id: string;
          role: EngagementMemberRole;
          can_invite: boolean;
        }>
      ).map((r) => [r.engagement_id, { role: r.role, can_invite: r.can_invite }]),
    );
    // Finding E6 — engagement ids are a `text` column minted client-side
    // (`eng-<ts>-<rand>`), NOT a DB-constrained uuid, so a row id could in
    // principle contain a quote/comma/paren. These ids are string-interpolated
    // into the PostgREST `.or()` filter below, where such characters would
    // break or alter the filter expression. Restrict the interpolated ids to a
    // safe charset (every legitimate id matches); drop anything else.
    const SAFE_ENGAGEMENT_ID = /^[A-Za-z0-9_-]+$/;
    const sharedIds = Array.from(memberMeta.keys()).filter((id) =>
      SAFE_ENGAGEMENT_ID.test(id),
    );

    // owner_id = uid OR id IN (sharedIds)
    let query = supabase
      .from(ENGAGEMENTS_TABLE)
      .select("data, owner_id, id")
      .eq("is_deleted", false);
    query =
      sharedIds.length > 0
        ? query.or(
            `owner_id.eq.${uid},id.in.(${sharedIds
              .map((id) => `"${id}"`)
              .join(",")})`,
          )
        : query.eq("owner_id", uid);

    const { data, error } = await query.order("updated_at", {
      ascending: false,
    });
    if (error) {
      throw new Error(`Supabase listEngagements failed: ${error.message}`);
    }

    const rows = (data ?? []) as Array<{
      data: EngagementHistoryEntry;
      owner_id: string | null;
      id: string;
    }>;
    // The `data` blob has no owner_id; map it from the top-level column.
    const rowOwnerById = new Map(rows.map((row) => [row.id, row.owner_id]));

    // Resolve owner emails for SHARED (not-owned) engagements via the
    // RLS-safe SECURITY DEFINER RPC, for the "Paylaşan: …" display line.
    const sharedOwnerIds = Array.from(
      new Set(
        rows
          .map((r) => r.owner_id)
          .filter((oid): oid is string => oid != null && oid !== uid),
      ),
    );
    const ownerEmailById = new Map<string, string | null>();
    if (sharedOwnerIds.length > 0) {
      const { data: ownerProfs, error: oErr } = await supabase.rpc(
        "lookup_profiles_by_ids",
        { p_ids: sharedOwnerIds },
      );
      if (oErr) {
        console.warn("[listEngagements] owner email lookup failed", oErr);
      } else {
        for (const p of (ownerProfs ?? []) as Array<{
          id: string;
          email: string | null;
        }>) {
          ownerEmailById.set(p.id, p.email);
        }
      }
    }

    const decorated: EngagementWithAccess[] = rows.map((row) => {
      const e = row.data;
      const owner = rowOwnerById.get(e.id) ?? null;
      const mm = memberMeta.get(e.id);
      return {
        ...e,
        _access: {
          isOwner: owner != null && owner === uid,
          myRole: mm?.role ?? null,
          myCanInvite: mm?.can_invite ?? false,
          ownerEmail:
            owner != null && owner !== uid
              ? (ownerEmailById.get(owner) ?? null)
              : null,
          // SPEC-ADMIN3 — the list view does not gate edit controls, so the
          // per-row admin flag is resolved on demand by getEngagementAccess.
          isAdmin: false,
        },
      };
    });
    return decorated;
  }

  async getEngagement(id: string): Promise<EngagementHistoryEntry | null> {
    const { data, error } = await supabase
      .from(ENGAGEMENTS_TABLE)
      .select("data")
      .eq("id", id)
      .eq("is_deleted", false)
      .maybeSingle();
    if (error) {
      throw new Error(`Supabase getEngagement failed: ${error.message}`);
    }
    if (!data) return null;
    return (data as { data: EngagementHistoryEntry }).data;
  }

  async saveEngagement(entry: EngagementHistoryEntry): Promise<void> {
    // SPEC-18b — owner_id is stamped DB-side from auth.uid(); guard
    // against an unauthenticated save that would create an owner-less
    // (orphan) row. The SPEC-17 route guard already blocks the app
    // pre-login, so this is belt-and-suspenders.
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) {
      throw new Error(
        "Engagement kaydedilemedi: oturum bulunamadı. Lütfen tekrar giriş yapın.",
      );
    }

    const { error } = await supabase
      .from(ENGAGEMENTS_TABLE)
      .upsert(toRow(entry), { onConflict: "id" });
    if (error) {
      throw new Error(`Supabase saveEngagement failed: ${error.message}`);
    }
    // SPEC-15b — best-effort read-only contracts mirror. Must not throw.
    await this.syncContractsMirror(entry);
  }

  /**
   * SPEC-15b — rebuild the `contracts` mirror table for this engagement.
   *
   * The mirror is a read-only convenience view of `entry.contracts` for
   * Supabase dashboard inspection; the tool NEVER reads from it.
   * `engagements.data` remains the single source of truth.
   *
   * Strategy: delete-then-insert per engagement so the mirror cannot
   * drift from `data`. Failure is swallowed (warn only) — the engagement
   * upsert already succeeded by the time we get here, and a mirror error
   * must never break the save path.
   */
  private async syncContractsMirror(
    entry: EngagementHistoryEntry,
  ): Promise<void> {
    try {
      const del = await supabase
        .from(CONTRACTS_TABLE)
        .delete()
        .eq("engagement_id", entry.id);
      if (del.error) {
        console.warn(
          `[contracts mirror] delete failed for ${entry.id}: ${del.error.message}`,
        );
        return;
      }

      const rows = entry.contracts
        .map((c, index) => ({ c, index }))
        .filter(
          (x): x is { c: NonNullable<typeof x.c>; index: number } =>
            x.c != null,
        )
        .map(({ c, index }) => ({
          engagement_id: entry.id,
          contract_index: index,
          contract_id: c.contractId ?? null,
          description: c.description ?? null,
          commencement_date: c.commencementDate || null,
          lease_term_months: c.leaseTermMonths ?? null,
          currency: c.currency ?? null,
          functional_currency:
            c.functionalCurrency ?? entry.functionalCurrency ?? null,
          payment_amount: c.payment?.amount ?? null,
          payment_frequency: c.payment?.frequency ?? null,
          payment_timing: c.payment?.timing ?? null,
          first_payment_date: c.payment?.firstPaymentDate || null,
          discount_rate_annual: c.discountRateAnnual ?? null,
          discount_rate_type: c.discountRateType ?? null,
          is_index_linked: c.indexLinked === true,
          has_modifications:
            Array.isArray(c.modifications) && c.modifications.length > 0,
          has_termination: c.termination != null,
          is_multi_currency:
            c.functionalCurrency != null &&
            c.functionalCurrency !== c.currency,
          client_name: entry.clientName ?? null,
        }));

      if (rows.length === 0) return;

      const ins = await supabase.from(CONTRACTS_TABLE).insert(rows);
      if (ins.error) {
        console.warn(
          `[contracts mirror] insert failed for ${entry.id}: ${ins.error.message}`,
        );
      }
    } catch (err) {
      console.warn(
        `[contracts mirror] unexpected error for ${entry.id}:`,
        err,
      );
    }
  }

  /**
   * Soft-delete with payload reclaim — mirrors LocalStorageHistoryStorage.
   *
   * Fetches the live row, mutates the entry exactly as the local version
   * does (clear `lastResult`, reset lifecycle to draft), then upserts the
   * full row back so both the top-level columns AND the `data` blob are
   * in sync.
   */
  async deleteEngagement(id: string): Promise<void> {
    const { data, error } = await supabase
      .from(ENGAGEMENTS_TABLE)
      .select("data")
      .eq("id", id)
      .maybeSingle();
    if (error) {
      throw new Error(`Supabase deleteEngagement fetch failed: ${error.message}`);
    }
    if (!data) return;
    const target = (data as { data: EngagementHistoryEntry }).data;
    const now = new Date().toISOString();
    target.isDeleted = true;
    target.deletedAt = now;
    target.updatedAt = now;
    target.lastResult = undefined;
    target.status = "draft";
    target.isStale = false;
    target.calculatedAt = undefined;
    target.staleSince = undefined;
    target.lastCalculateError = undefined;
    await this.saveEngagement(target);
  }

  async purgeOldDeleted(): Promise<number> {
    const cutoffIso = new Date(
      Date.now() - PURGE_AFTER_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();
    const { data, error } = await supabase
      .from(ENGAGEMENTS_TABLE)
      .delete()
      .eq("is_deleted", true)
      .lt("deleted_at", cutoffIso)
      .select("id");
    if (error) {
      throw new Error(`Supabase purgeOldDeleted failed: ${error.message}`);
    }
    return (data ?? []).length;
  }

  /**
   * Per-contract hard delete — ported 1:1 from LocalStorageHistoryStorage
   * (Phase 6e.4.2-hotfix-2 Bug D). Pure in-memory entry manipulation,
   * persisted via Supabase upsert at the end.
   */
  async deleteContract(
    engagementId: string,
    contractIndex: number,
  ): Promise<EngagementHistoryEntry> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) {
      throw new Error(`Engagement not found: ${engagementId}`);
    }
    if (
      contractIndex < 0 ||
      contractIndex >= engagement.contracts.length
    ) {
      throw new Error(
        `Contract index out of range: ${contractIndex} (engagement has ${engagement.contracts.length} slots)`,
      );
    }

    const nextContracts = [...engagement.contracts];
    nextContracts.splice(contractIndex, 1);

    const nextDirty = engagement.contractsDirty
      ? [...engagement.contractsDirty]
      : undefined;
    nextDirty?.splice(contractIndex, 1);

    const nextErrors = reindexAfterRemoval(
      engagement.lastCalculateError,
      contractIndex,
    );

    const cleanedEngagement = engagement as unknown as Record<string, unknown>;
    if ("contractDeletions" in cleanedEngagement) {
      delete cleanedEngagement.contractDeletions;
    }

    const updated = transitionToStale({
      ...engagement,
      contracts: nextContracts,
      contractsDirty: nextDirty,
      contractCount: nextContracts.length,
      lastCalculateError: nextErrors,
      updatedAt: new Date().toISOString(),
    });
    await this.saveEngagement(updated);
    return updated;
  }

  // ── Admin reads (SPEC-23) ─────────────────────────────────────────────

  async listEngagementsByOwner(
    ownerId: string,
  ): Promise<EngagementHistoryEntry[]> {
    const { data, error } = await supabase
      .from(ENGAGEMENTS_TABLE)
      .select("data")
      .eq("is_deleted", false)
      .eq("owner_id", ownerId)
      .order("updated_at", { ascending: false });
    if (error) {
      throw new Error(
        `Supabase listEngagementsByOwner failed: ${error.message}`,
      );
    }
    return ((data ?? []) as Array<{ data: EngagementHistoryEntry }>).map(
      (row) => row.data,
    );
  }

  async listUserProfiles(): Promise<UserProfile[]> {
    const { data, error } = await supabase
      .from(PROFILES_TABLE)
      .select("id, email, full_name, role, tier, created_at")
      .order("created_at", { ascending: true });
    if (error) {
      throw new Error(`Supabase listUserProfiles failed: ${error.message}`);
    }
    return (
      (data ?? []) as Array<{
        id: string;
        email: string | null;
        full_name: string | null;
        role: string;
        tier: string | null;
        created_at: string;
      }>
    ).map((row) => ({
      id: row.id,
      email: row.email,
      fullName: row.full_name,
      role: row.role,
      // SPEC-TIER Part C — defensive default; the DB column is NOT NULL
      // DEFAULT 'default', so a null only arises from a malformed row.
      tier: row.tier ?? "default",
      createdAt: row.created_at,
    }));
  }

  async setUserTier(userId: string, tier: string): Promise<void> {
    const { error } = await supabase.rpc("set_user_tier", {
      p_user_id: userId,
      p_tier: tier,
    });
    if (error) {
      // The RPC raises bare sentinels (RAISE EXCEPTION 'NOT_ADMIN' etc.);
      // PostgREST surfaces them in error.message. Re-throw a stable, typed
      // sentinel the UI maps to an i18n message. Unknown errors pass through.
      const m = error.message ?? "";
      if (m.includes("NOT_ADMIN")) throw new Error("TIER_NOT_ADMIN");
      if (m.includes("UNLIMITED_ADMIN_ONLY")) throw new Error("TIER_UNLIMITED_ADMIN_ONLY");
      if (m.includes("INVALID_TIER")) throw new Error("TIER_INVALID");
      if (m.includes("USER_NOT_FOUND")) throw new Error("TIER_USER_NOT_FOUND");
      throw new Error(`Supabase setUserTier failed: ${error.message}`);
    }
  }

  // ── Sharing (SPEC-SHARE) ──────────────────────────────────────────────

  async listMembers(engagementId: string): Promise<EngagementMember[]> {
    const { data, error } = await supabase
      .from(MEMBERS_TABLE)
      .select("engagement_id, user_id, role, can_invite, created_at")
      .eq("engagement_id", engagementId)
      .order("created_at", { ascending: true });
    if (error) {
      throw new Error(`Supabase listMembers failed: ${error.message}`);
    }
    const rows = (data ?? []) as Array<{
      engagement_id: string;
      user_id: string;
      role: EngagementMemberRole;
      can_invite: boolean;
      created_at: string;
    }>;
    if (rows.length === 0) return [];

    // Resolve email/name via RLS-safe SECURITY DEFINER RPC (profiles RLS
    // would otherwise hide other members' rows from the owner).
    const ids = rows.map((r) => r.user_id);
    const { data: profs, error: pErr } = await supabase.rpc(
      "lookup_profiles_by_ids",
      { p_ids: ids },
    );
    if (pErr) {
      throw new Error(
        `Supabase listMembers (profiles) failed: ${pErr.message}`,
      );
    }
    const profMap = new Map(
      (
        (profs ?? []) as Array<{
          id: string;
          email: string | null;
          full_name: string | null;
        }>
      ).map((p) => [p.id, p]),
    );
    return rows.map((r) => ({
      engagementId: r.engagement_id,
      userId: r.user_id,
      role: r.role,
      canInvite: r.can_invite,
      createdAt: r.created_at,
      email: profMap.get(r.user_id)?.email ?? null,
      fullName: profMap.get(r.user_id)?.full_name ?? null,
    }));
  }

  async addMemberByEmail(
    engagementId: string,
    email: string,
    role: EngagementMemberRole,
    canInvite: boolean,
  ): Promise<void> {
    const normalized = email.trim().toLowerCase();
    // Resolve email → user id via RLS-safe SECURITY DEFINER RPC
    // (profiles RLS would otherwise hide other users' rows).
    const { data: foundId, error: pErr } = await supabase.rpc(
      "lookup_user_id_by_email",
      { p_email: normalized },
    );
    if (pErr) {
      const m = pErr.message ?? "";
      if (m.includes("RATE_LIMIT_EXCEEDED")) {
        throw new Error("SHARE_RATE_LIMIT");
      }
      if (m.includes("AUTH_REQUIRED")) {
        throw new Error("SHARE_AUTH_REQUIRED");
      }
      throw new Error(
        `Supabase addMemberByEmail (lookup) failed: ${pErr.message}`,
      );
    }
    if (!foundId) {
      // Typed-ish sentinel the UI can match on.
      throw new Error("SHARE_USER_NOT_FOUND");
    }
    const userId = foundId as string;

    const { error } = await supabase.from(MEMBERS_TABLE).upsert(
      {
        engagement_id: engagementId,
        user_id: userId,
        role,
        can_invite: canInvite,
      },
      { onConflict: "engagement_id,user_id" },
    );
    if (error) {
      throw new Error(`Supabase addMemberByEmail failed: ${error.message}`);
    }
  }

  async updateMember(
    engagementId: string,
    userId: string,
    role: EngagementMemberRole,
    canInvite: boolean,
  ): Promise<void> {
    const { error } = await supabase
      .from(MEMBERS_TABLE)
      .update({ role, can_invite: canInvite })
      .eq("engagement_id", engagementId)
      .eq("user_id", userId);
    if (error) {
      throw new Error(`Supabase updateMember failed: ${error.message}`);
    }
  }

  async removeMember(engagementId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from(MEMBERS_TABLE)
      .delete()
      .eq("engagement_id", engagementId)
      .eq("user_id", userId);
    if (error) {
      throw new Error(`Supabase removeMember failed: ${error.message}`);
    }
  }

  async getEngagementAccess(
    engagementId: string,
  ): Promise<EngagementAccessMeta | null> {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) return null;

    // Owner row (also confirms visibility under RLS).
    const { data: engRow, error: engErr } = await supabase
      .from(ENGAGEMENTS_TABLE)
      .select("owner_id")
      .eq("id", engagementId)
      .eq("is_deleted", false)
      .maybeSingle();
    if (engErr) {
      throw new Error(`Supabase getEngagementAccess failed: ${engErr.message}`);
    }
    if (!engRow) return null; // not visible / not found
    const ownerId = (engRow as { owner_id: string | null }).owner_id;
    const isOwner = ownerId != null && ownerId === uid;

    // My membership row (if any).
    const { data: memRow, error: memErr } = await supabase
      .from(MEMBERS_TABLE)
      .select("role, can_invite")
      .eq("engagement_id", engagementId)
      .eq("user_id", uid)
      .maybeSingle();
    if (memErr) {
      throw new Error(
        `Supabase getEngagementAccess (member) failed: ${memErr.message}`,
      );
    }
    const mm = memRow as {
      role: EngagementMemberRole;
      can_invite: boolean;
    } | null;

    // SPEC-ADMIN3 — probe platform-admin status so the UI unlocks
    // Calculate/edit/add/delete on ANY engagement for admins (the DB RLS
    // already permits it via `... OR is_admin()`). Fail-safe: any error or
    // malformed response leaves isAdmin=false — never grant extra access on a
    // failed probe. Never throws.
    let isAdmin = false;
    try {
      const { data: adminFlag, error: adminErr } =
        await supabase.rpc("is_admin");
      if (adminErr) {
        console.warn(
          "[getEngagementAccess] is_admin probe error; treating as non-admin",
          adminErr.message,
        );
      } else {
        isAdmin = adminFlag === true;
      }
    } catch (err) {
      console.warn(
        "[getEngagementAccess] is_admin probe threw; treating as non-admin",
        err,
      );
    }

    // Owner email only when shared (not owned by me).
    let ownerEmail: string | null = null;
    if (!isOwner && ownerId) {
      const { data: profs, error: pErr } = await supabase.rpc(
        "lookup_profiles_by_ids",
        { p_ids: [ownerId] },
      );
      if (!pErr) {
        const row = ((profs ?? []) as Array<{
          id: string;
          email: string | null;
        }>)[0];
        ownerEmail = row?.email ?? null;
      }
    }

    return {
      isOwner,
      myRole: mm?.role ?? null,
      myCanInvite: mm?.can_invite ?? false,
      ownerEmail,
      isAdmin,
    };
  }

  // ── Admin usage analytics (SPEC-ADMIN3) ─────────────────────────────────
  async getUsageStats(days: number): Promise<UsageStats> {
    const { data, error } = await supabase.rpc("admin_usage_stats", {
      p_days: days,
    });
    if (error) {
      throw new Error(`Supabase getUsageStats failed: ${error.message}`);
    }
    // The RPC RETURNS TABLE(...), so PostgREST yields an array of one row.
    const row = (Array.isArray(data) ? data[0] : data) as
      | {
          total_computes: number;
          total_contracts: number;
          single_computes: number;
          engagement_computes: number;
          active_users: number;
          window_days: number;
        }
      | undefined;
    return {
      totalComputes: Number(row?.total_computes ?? 0),
      totalContracts: Number(row?.total_contracts ?? 0),
      singleComputes: Number(row?.single_computes ?? 0),
      engagementComputes: Number(row?.engagement_computes ?? 0),
      activeUsers: Number(row?.active_users ?? 0),
      windowDays: Number(row?.window_days ?? days),
    };
  }

  async listComputeLog(days: number): Promise<ComputeLogEntry[]> {
    const cutoff = new Date(
      Date.now() - days * 24 * 60 * 60 * 1000,
    ).toISOString();
    const { data, error } = await supabase
      .from("compute_log")
      .select(
        "id, endpoint, engagement_id, contract_count, max_months, created_at",
      )
      .gte("created_at", cutoff)
      .order("created_at", { ascending: false });
    if (error) {
      throw new Error(`Supabase listComputeLog failed: ${error.message}`);
    }
    return (
      (data ?? []) as Array<{
        id: number;
        endpoint: "single" | "engagement";
        engagement_id: string | null;
        contract_count: number;
        max_months: number;
        created_at: string;
      }>
    ).map((r) => ({
      id: r.id,
      endpoint: r.endpoint,
      engagementId: r.engagement_id,
      contractCount: r.contract_count,
      maxMonths: r.max_months,
      createdAt: r.created_at,
    }));
  }

  // ── Settings — own profile (SPEC-SETTINGS) ──────────────────────────────

  async getMyProfile(): Promise<UserProfile> {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) {
      // Mirror saveEngagement's session guard — no caller id, no read.
      throw new Error("SETTINGS_AUTH_REQUIRED");
    }
    const { data, error } = await supabase
      .from(PROFILES_TABLE)
      .select("id, email, full_name, role, tier, created_at")
      .eq("id", uid)
      .single();
    if (error) {
      throw new Error(`Supabase getMyProfile failed: ${error.message}`);
    }
    const row = data as {
      id: string;
      email: string | null;
      full_name: string | null;
      role: string;
      tier: string | null;
      created_at: string;
    };
    return {
      id: row.id,
      email: row.email,
      fullName: row.full_name,
      role: row.role,
      // Defensive default — the DB column is NOT NULL DEFAULT 'default'.
      tier: row.tier ?? "default",
      createdAt: row.created_at,
    };
  }

  async updateMyFullName(fullName: string): Promise<void> {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) {
      throw new Error("SETTINGS_AUTH_REQUIRED");
    }
    const { error } = await supabase
      .from(PROFILES_TABLE)
      .update({ full_name: fullName })
      .eq("id", uid);
    if (error) {
      throw new Error(`Supabase updateMyFullName failed: ${error.message}`);
    }
  }

  // ── Settings — tier change requests (SPEC-SETTINGS) ─────────────────────

  async requestTierChange(requestedTier: string, note?: string): Promise<void> {
    const { error } = await supabase.rpc("request_tier_change", {
      p_requested_tier: requestedTier,
      p_note: note ?? null,
    });
    if (error) {
      // The RPC raises bare sentinels; PostgREST surfaces them in
      // error.message. Re-throw a stable, typed sentinel the UI maps to an
      // i18n message (mirrors setUserTier). Unknown errors pass through.
      const m = error.message ?? "";
      if (m.includes("AUTH_REQUIRED")) throw new Error("TIERREQ_AUTH_REQUIRED");
      if (m.includes("INVALID_TIER")) throw new Error("TIERREQ_INVALID_TIER");
      if (m.includes("NOTE_TOO_LONG")) throw new Error("TIERREQ_NOTE_TOO_LONG");
      if (m.includes("PROFILE_NOT_FOUND"))
        throw new Error("TIERREQ_PROFILE_NOT_FOUND");
      if (m.includes("ALREADY_ON_TIER"))
        throw new Error("TIERREQ_ALREADY_ON_TIER");
      if (m.includes("PENDING_REQUEST_EXISTS"))
        throw new Error("TIERREQ_PENDING_EXISTS");
      throw new Error(`Supabase requestTierChange failed: ${error.message}`);
    }
  }

  async cancelTierChange(requestId: string): Promise<void> {
    const { error } = await supabase.rpc("cancel_tier_change", {
      p_request_id: requestId,
    });
    if (error) {
      const m = error.message ?? "";
      if (m.includes("AUTH_REQUIRED")) throw new Error("TIERREQ_AUTH_REQUIRED");
      if (m.includes("REQUEST_NOT_FOUND"))
        throw new Error("TIERREQ_NOT_FOUND");
      if (m.includes("NOT_OWNER")) throw new Error("TIERREQ_NOT_OWNER");
      if (m.includes("NOT_PENDING")) throw new Error("TIERREQ_NOT_PENDING");
      throw new Error(`Supabase cancelTierChange failed: ${error.message}`);
    }
  }

  async listMyTierRequests(): Promise<TierChangeRequest[]> {
    // RLS scopes the query to the caller's own rows.
    const { data, error } = await supabase
      .from(TIER_REQUESTS_TABLE)
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      throw new Error(`Supabase listMyTierRequests failed: ${error.message}`);
    }
    return ((data ?? []) as TierRequestRow[]).map(toTierChangeRequest);
  }

  async listPendingTierRequests(): Promise<PendingTierRequest[]> {
    const { data, error } = await supabase
      .from(TIER_REQUESTS_TABLE)
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    if (error) {
      throw new Error(
        `Supabase listPendingTierRequests failed: ${error.message}`,
      );
    }
    const rows = ((data ?? []) as TierRequestRow[]).map(toTierChangeRequest);
    if (rows.length === 0) return [];

    // Resolve requester email/name via the RLS-safe SECURITY DEFINER RPC
    // (profiles RLS would otherwise hide other users' rows from the admin).
    const ids = Array.from(new Set(rows.map((r) => r.userId)));
    const { data: profs, error: pErr } = await supabase.rpc(
      "lookup_profiles_by_ids",
      { p_ids: ids },
    );
    if (pErr) {
      throw new Error(
        `Supabase listPendingTierRequests (profiles) failed: ${pErr.message}`,
      );
    }
    const profMap = new Map(
      (
        (profs ?? []) as Array<{
          id: string;
          email: string | null;
          full_name: string | null;
        }>
      ).map((p) => [p.id, p]),
    );
    return rows.map((r) => ({
      ...r,
      requesterEmail: profMap.get(r.userId)?.email ?? null,
      requesterFullName: profMap.get(r.userId)?.full_name ?? null,
    }));
  }

  async reviewTierChange(
    requestId: string,
    approve: boolean,
    reviewNote?: string,
  ): Promise<void> {
    const { error } = await supabase.rpc("review_tier_change", {
      p_request_id: requestId,
      p_approve: approve,
      p_review_note: reviewNote ?? null,
    });
    if (error) {
      const m = error.message ?? "";
      if (m.includes("AUTH_REQUIRED")) throw new Error("TIERREQ_AUTH_REQUIRED");
      if (m.includes("NOT_ADMIN")) throw new Error("TIERREQ_NOT_ADMIN");
      if (m.includes("REQUEST_NOT_FOUND"))
        throw new Error("TIERREQ_NOT_FOUND");
      if (m.includes("NOT_PENDING")) throw new Error("TIERREQ_NOT_PENDING");
      throw new Error(`Supabase reviewTierChange failed: ${error.message}`);
    }
  }
}
